import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { BillingService, type CreditBalance } from '../services/billingService';
import { queryKeys } from '../queries/keys';
import { useAuthStore } from '../store/authStore';
import { TopUpDrawer } from './TopUpDrawer';
import { readShortfall, type CreditShortfall } from './insufficientCredits';

interface CreditsContextValue {
  balance: CreditBalance | undefined;
  /** False while billing is in shadow mode — show prices as estimates only. */
  enforced: boolean;
  /** Open the top-up drawer with nothing blocked behind it. */
  openTopUp: () => void;
  /**
   * Handle a refused-for-credit error: opens the drawer sized to the shortfall
   * and remembers `retry`, which runs by itself once credit lands. Returns true
   * when it took ownership, so the caller can suppress its own error UI.
   */
  handleInsufficientCredits: (err: unknown, retry?: () => void) => boolean;
  /** Pull a fresh balance — after spending, or on returning from checkout. */
  refreshBalance: () => void;
}

/**
 * Permissive fallback so components work with no provider mounted — the lab
 * tests render panels in isolation and must not have to know about billing.
 */
const FALLBACK: CreditsContextValue = {
  balance: undefined,
  enforced: false,
  openTopUp: () => {},
  handleInsufficientCredits: () => false,
  refreshBalance: () => {},
};

const CreditsContext = createContext<CreditsContextValue>(FALLBACK);

export const useCredits = (): CreditsContextValue => useContext(CreditsContext);

/**
 * Credits gate and top-up host.
 *
 * The design goal is that paying never interrupts work: checkout opens in
 * another tab, this app keeps its state, and the operation that ran out of
 * credit re-runs itself when the money arrives. So the drawer lives here, at the
 * app root, rather than inside any lab — unmounting a lab to show a payment
 * screen is exactly what we're avoiding.
 */
export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isSignedIn = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [shortfall, setShortfall] = useState<CreditShortfall | null>(null);

  // A ref, not state: it's a one-shot continuation, and re-rendering the whole
  // app because a callback was stashed would be pointless.
  const pendingRetry = useRef<(() => void) | null>(null);

  const balanceQuery = useQuery({
    queryKey: queryKeys.creditBalance(),
    queryFn: () => BillingService.getBalance(),
    enabled: isSignedIn,
    staleTime: 15_000,
  });

  const refreshBalance = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.creditBalance() });
  }, [queryClient]);

  const openTopUp = useCallback(() => {
    setShortfall(null);
    pendingRetry.current = null;
    setOpen(true);
  }, []);

  const handleInsufficientCredits = useCallback((err: unknown, retry?: () => void) => {
    const detail = readShortfall(err);
    if (!detail) return false;

    pendingRetry.current = retry ?? null;
    setShortfall(detail);
    setOpen(true);
    return true;
  }, []);

  /** Credit arrived. Close, then resume whatever was blocked. */
  const handleFunded = useCallback(() => {
    refreshBalance();
    const retry = pendingRetry.current;
    pendingRetry.current = null;
    setOpen(false);
    setShortfall(null);
    // After the drawer has gone, so the user sees their work resume rather than
    // a spinner behind a panel that is still closing.
    if (retry) setTimeout(retry, 150);
  }, [refreshBalance]);

  const handleClose = useCallback(() => {
    // Dismissing without paying abandons the blocked operation. Re-running it
    // would just fail again, and the user has already seen why.
    pendingRetry.current = null;
    setOpen(false);
    setShortfall(null);
  }, []);

  const value = useMemo<CreditsContextValue>(
    () => ({
      balance: balanceQuery.data,
      enforced: balanceQuery.data?.enforced ?? false,
      openTopUp,
      handleInsufficientCredits,
      refreshBalance,
    }),
    [balanceQuery.data, openTopUp, handleInsufficientCredits, refreshBalance]
  );

  return (
    <CreditsContext.Provider value={value}>
      {children}
      <TopUpDrawer
        open={open}
        shortfall={shortfall}
        balanceNgn={balanceQuery.data?.balanceNgn ?? 0}
        willResumeWork={pendingRetry.current !== null}
        onFunded={handleFunded}
        onClose={handleClose}
      />
    </CreditsContext.Provider>
  );
};
